import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Phone, AlertCircle, CheckCircle, Lock, MessageSquare, ArrowLeft } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import Footer from '../shared/Footer';

const API_BASE_URL = getApiBaseUrl();

// 验证函数
const validatePhone = (phone) => {
  const trimmed = phone.trim();
  if (!trimmed) return '请输入手机号';
  if (!/^1\d{10}$/.test(trimmed)) return '请输入11位中国大陆手机号';
  return '';
};

const validateSmsCode = (code) => {
  const trimmed = code.trim();
  if (!trimmed) return '请输入短信验证码';
  if (!/^\d{4,6}$/.test(trimmed)) return '验证码格式不正确';
  return '';
};

const validatePassword = (password) => {
  if (!password) return '请输入密码';
  if (password.length < 8) return '密码至少需要8个字符';
  if (password.length > 100) return '密码不能超过100个字符';
  if (!/[A-Z]/.test(password)) return '密码必须包含至少一个大写字母';
  if (!/[a-z]/.test(password)) return '密码必须包含至少一个小写字母';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\\"]/.test(password)) return '密码必须包含至少一个特殊字符';
  return '';
};

const ResetPasswordForm = ({ onNavigateToLogin }) => {
  const navigate = useNavigate();

  // 步骤：1=输入手机号, 2=验证验证码, 3=设置新密码
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsMessage, setSmsMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 倒计时效果
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 处理输入变化
  const handleInputChange = (field, value) => {
    switch (field) {
      case 'phone':
        setPhone(value);
        break;
      case 'smsCode':
        setSmsCode(value);
        break;
      case 'newPassword':
        setNewPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      default:
        break;
    }

    // 清除对应字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // 第1步：发送验证码
  const handleStep1Next = async () => {
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setErrors(prev => ({ ...prev, phone: phoneError }));
      return;
    }

    // 发送验证码
    setIsSendingCode(true);
    setSmsMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phone.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.detail || data.message || '验证码发送失败';
        setErrors(prev => ({ ...prev, smsCode: message }));
        return;
      }

      setSmsMessage('验证码已发送，请注意查收');
      setCountdown(data.interval || 60);

      // 发送成功后，自动进入第2步
      setStep(2);
      setErrors({});
    } catch (error) {
      setErrors(prev => ({ ...prev, smsCode: '验证码发送失败，请稍后再试' }));
    } finally {
      setIsSendingCode(false);
    }
  };

  // 第2步：验证验证码
  const handleVerifyCode = async () => {
    const smsCodeError = validateSmsCode(smsCode);
    if (smsCodeError) {
      setErrors(prev => ({ ...prev, smsCode: smsCodeError }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          phone: phone.trim(),
          sms_code: smsCode.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors(prev => ({ ...prev, general: data.detail || '验证码验证失败' }));
        return;
      }

      // 验证成功，进入第3步
      setStep(3);
      setErrors({});
    } catch (error) {
      setErrors(prev => ({ ...prev, general: '验证失败，请稍后重试' }));
    } finally {
      setIsLoading(false);
    }
  };

  // 第2步：重新发送验证码
  const handleResendCode = async () => {
    setIsSendingCode(true);
    setSmsMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phone.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.detail || data.message || '验证码发送失败';
        setErrors(prev => ({ ...prev, smsCode: message }));
        return;
      }

      setSmsMessage('验证码已重新发送，请注意查收');
      setCountdown(data.interval || 60);
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.smsCode;
        return newErrors;
      });
    } catch (error) {
      setErrors(prev => ({ ...prev, smsCode: '验证码发送失败，请稍后再试' }));
    } finally {
      setIsSendingCode(false);
    }
  };

  // 第3步：提交新密码
  const handleResetPassword = async () => {
    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      setErrors(prev => ({ ...prev, newPassword: newPasswordError }));
      return;
    }

    if (!confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '请确认密码' }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '两次输入的密码不一致' }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          phone: phone.trim(),
          sms_code: smsCode.trim(),
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors(prev => ({ ...prev, general: data.detail || '密码重置失败' }));
        return;
      }

      // 重置成功，自动跳转到首页（如果已自动登录）
      // 否则提示用户使用新密码登录
      alert('密码重置成功！');
      navigate('/');
    } catch (error) {
      setErrors(prev => ({ ...prev, general: '密码重置失败，请稍后重试' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 标题 */}
        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">重置密码</h1>
          <p className="text-gray-600">验证身份后设置新密码</p>
        </div>

        {/* 进度指示器 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold ${step >= 1 ? 'bg-blue-600' : 'bg-gray-300'}`}>
              1
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}>
              2
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}>
              3
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>验证手机号</span>
            <span>验证码</span>
            <span>新密码</span>
          </div>
        </div>

        {/* 表单 */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* 第1步：输入手机号 */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  手机号
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="请输入手机号码"
                    disabled={isLoading}
                    maxLength={11}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  {errors.general}
                </div>
              )}

              <button
                onClick={handleStep1Next}
                disabled={isSendingCode || !phone || errors.phone}
                className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                  isSendingCode || !phone || errors.phone
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {isSendingCode ? '发送中...' : '发送验证码'}
              </button>
            </div>
          )}

          {/* 第2步：验证短信验证码 */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  验证码已发送至 <span className="font-semibold">{phone}</span>
                </p>
              </div>

              <div>
                <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700 mb-2">
                  短信验证码
                </label>
                <div className="flex space-x-4">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="smsCode"
                      type="text"
                      value={smsCode}
                      onChange={(e) => handleInputChange('smsCode', e.target.value)}
                      className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.smsCode ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="输入验证码"
                      disabled={isLoading}
                      maxLength={6}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isSendingCode || countdown > 0 || isLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isSendingCode || countdown > 0
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {countdown > 0 ? `${countdown}s` : '重新发送'}
                  </button>
                </div>
                {errors.smsCode && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.smsCode}
                  </p>
                )}
              </div>

              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  {errors.general}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  上一步
                </button>
                <button
                  onClick={handleVerifyCode}
                  disabled={isLoading || !smsCode || errors.smsCode}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                    isLoading || !smsCode || errors.smsCode
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  }`}
                >
                  {isLoading ? '验证中...' : '验证验证码'}
                </button>
              </div>
            </div>
          )}

          {/* 第3步：设置新密码 */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  新密码
                  <span className="text-xs text-gray-500 ml-1">(8-100个字符)</span>
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    className={`block w-full px-3 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="请输入新密码"
                    disabled={isLoading}
                    minLength={8}
                    maxLength={100}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.newPassword}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  确认密码
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`block w-full px-3 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="请再次输入密码"
                    disabled={isLoading}
                    maxLength={100}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                  {confirmPassword && newPassword === confirmPassword && !errors.confirmPassword && (
                    <div className="absolute inset-y-0 right-10 pr-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  )}
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* 密码要求 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">密码要求：</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`} />
                    至少8个字符
                  </li>
                  <li className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${/[A-Z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`} />
                    包含至少一个大写字母
                  </li>
                  <li className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${/[a-z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`} />
                    包含至少一个小写字母
                  </li>
                  <li className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\\"]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`} />
                    包含至少一个特殊字符
                  </li>
                </ul>
              </div>

              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  {errors.general}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  上一步
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={isLoading || !newPassword || !confirmPassword || errors.newPassword || errors.confirmPassword}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                    isLoading || !newPassword || !confirmPassword || errors.newPassword || errors.confirmPassword
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  }`}
                >
                  {isLoading ? '重置中...' : '完成重置'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 返回登录 */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/auth/login')}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            返回登录
          </button>
        </div>
      </div>
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
};

export default ResetPasswordForm;
